import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import raygain from '../assets/images/raygain-logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import AuthContext from "../context/AuthContext";
import LogoutIcon from "@mui/icons-material/Logout";

// Import Material Icons for SSO Apps
import ContactsIcon from '@mui/icons-material/Contacts';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { getCompanyLogo, getUserName, getUserRole } from "../helpers/tokenService";
import API_BASE_URL from "../helpers/apiConfig";

// Custom colorful SSO grid icon
const RaygainSSOIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <rect x="4" y="4" width="6" height="6" rx="1.5" fill="#3b82f6" />
    <rect x="14" y="4" width="6" height="6" rx="1.5" fill="#10b981" />
    <rect x="4" y="14" width="6" height="6" rx="1.5" fill="#f59e0b" />
    <rect x="14" y="14" width="6" height="6" rx="1.5" fill="#ef4444" />
  </svg>
);

const ssoCategories = [
  {
    category: "Sales & Marketing",
    apps: [
      { name: "Sales CRM", desc: "CRM for small businesses", color: "linear-gradient(135deg, #8b5cf6, #6d28d9)", icon: <ContactsIcon style={{ fontSize: '18px' }} />, link: "https://crm.raygain.com/login" },
      { name: "Clientra", desc: "Client onboarding portal", color: "linear-gradient(135deg, #f97316, #ea580c)", icon: <GroupIcon style={{ fontSize: '18px' }} />, link: "#" }
    ]
  },
  {
    category: "Finance & Management",
    apps: [
      { name: "Projexa", desc: "Project management software", color: "linear-gradient(135deg, #3b82f6, #2563eb)", icon: <AssignmentIcon style={{ fontSize: '18px' }} />, link: "https://projexa.raygain.com/login" },
      { name: "Finexa", desc: "Financial planning & expenses", color: "linear-gradient(135deg, #10b981, #059669)", icon: <AccountBalanceWalletIcon style={{ fontSize: '18px' }} />, link: "https://finexa.raygain.com/login" }
    ]
  },
  {
    category: "Email & Collaboration",
    apps: [
      { name: "ITSM", desc: "IT Service Management & Helpdesk", color: "linear-gradient(135deg, #ec4899, #db2777)", icon: <SupportAgentIcon style={{ fontSize: '18px' }} />, link: "https://itsm.raygain.com/" }
    ]
  }
];

// Search items database matching all project screens
const searchItems = [
  // Shared
  { label: "Dashboard", category: "Overview", route: "/dashboard", roles: ["admin", "employee"] },
  { label: "My Profile", category: "Profile", route: "/profile", roles: ["employee"] },
  { label: "Announcements", category: "Employees", route: "/employees/announcements", roles: ["admin", "employee"] },
  { label: "Active Jobs", category: "On Board", route: "/active-jobs", roles: ["admin", "employee"] },

  // Admin specific
  { label: "Offer Letter", category: "On Board", route: "/onboard/offer-letter", roles: ["admin"] },
  { label: "Document Entry", category: "On Board", route: "/onboard/document-entry", roles: ["admin"] },
  { label: "Assets Details", category: "On Board", route: "/onboard/assets-details", roles: ["admin"] },
  { label: "Assets Assignment", category: "On Board", route: "/onboard/assets-assignment", roles: ["admin"] },
  { label: "Referral Requests", category: "On Board", route: "/onboard/referral-requests", roles: ["admin"] },
  { label: "All On Boarding", category: "On Board", route: "/onboard/all", roles: ["admin"] },
  { label: "Employee Master", category: "Employees", route: "/employees/employee-master", roles: ["admin"] },
  { label: "Profile Requests", category: "Employees", route: "/employees/profile-requests", roles: ["admin"] },
  { label: "Salary Master", category: "Employees", route: "/employees/salary-master", roles: ["admin"] },
  { label: "Category Settings", category: "Employees", route: "/employees/category", roles: ["admin"] },
  { label: "Calendar", category: "Employees", route: "/employees/calendar", roles: ["admin"] },
  { label: "All Employees List", category: "Employees", route: "/employees/all", roles: ["admin"] },
  { label: "Attendance Regularization", category: "Attendance", route: "/attendance/regularization", roles: ["admin"] },
  { label: "In Out Activities Report", category: "Attendance", route: "/attendance/in-out-activities", roles: ["admin"] },
  { label: "Daily Detail Attendance Report", category: "Attendance", route: "/attendance/daily-attendance", roles: ["admin"] },
  { label: "Manage Shift", category: "Attendance", route: "/attendance/manage-shift", roles: ["admin"] },
  { label: "All Attendance Records", category: "Attendance", route: "/attendance/all", roles: ["admin"] },
  { label: "Leave Balance Report", category: "Leave", route: "/leave/balance", roles: ["admin"] },
  { label: "Leave Policy", category: "Leave", route: "/leave/policy", roles: ["admin"] },
  { label: "All Leave Applications", category: "Leave", route: "/leave/all", roles: ["admin"] },
  { label: "Salary Register", category: "Payroll", route: "/payroll/salary-register", roles: ["admin"] },
  { label: "Salary Process", category: "Payroll", route: "/payroll/salary-process", roles: ["admin"] },
  { label: "Bank Transfer Disbursement", category: "Payroll", route: "/payroll/bank-transfer", roles: ["admin"] },
  { label: "Add Bank Details", category: "Payroll", route: "/payroll/add-bank-details", roles: ["admin"] },
  { label: "Salary PaySlip", category: "Payroll", route: "/payroll/salary-payslip", roles: ["admin"] },
  { label: "Final Settlement", category: "Payroll", route: "/payroll/final-settlement", roles: ["admin"] },
  { label: "IT Declaration", category: "Payroll", route: "/payroll/it-declaration", roles: ["admin"] },
  { label: "Prof. Tax Statement", category: "Payroll", route: "/payroll/tax-statement", roles: ["admin"] },
  { label: "ESIC Uploadable File", category: "Payroll", route: "/payroll/esic-uploadable", roles: ["admin"] },
  { label: "All Payroll History", category: "Payroll", route: "/payroll/all", roles: ["admin"] },
  { label: "EPFO Settings", category: "Complains", route: "/complains/epf", roles: ["admin"] },
  { label: "ESIC Settings", category: "Complains", route: "/complains/esic", roles: ["admin"] },
  { label: "Loan Application & Register", category: "Loans", route: "/loans", roles: ["admin"] },
  { label: "Survey All", category: "Survey", route: "/survey", roles: ["admin"] },
  { label: "General Settings", category: "Settings", route: "/settings", roles: ["admin"] },

  // Employee specific
  { label: "Punch In/Out Time", category: "Attendance", route: "/punch", roles: ["employee"] },
  { label: "Attendance Regularization", category: "Attendance", route: "/attendance/regularization", roles: ["employee"] },
  { label: "Apply Leave", category: "Leave", route: "/leave/apply", roles: ["employee"] },
  { label: "Approvals Hub", category: "Approvals", route: "/approvals", roles: ["employee"] },
  { label: "Approved Leaves History", category: "Approvals", route: "/approved-leaves", roles: ["employee"] },
  { label: "Salary Slip Download", category: "Payroll", route: "/payroll/salary-payslip", roles: ["employee"] },
  { label: "Change Password", category: "Profile", route: "/change-password", roles: ["employee"] },
  { label: "Holiday Calendar", category: "Leave", route: "/holidays", roles: ["employee"] },
  { label: "Leave Policy", category: "Leave", route: "/leave/policy", roles: ["employee"] },
  { label: "Referral Requests", category: "On Board", route: "/referral-request", roles: ["employee"] }
];

const Header = ({ toggle, setToggle, setCurrentTab }) => {
  const navigate = useNavigate();
  const userRole = getUserRole();
  const searchRef = useRef(null);

  const [circlename, setCircleName] = useState(userRole === 'admin' ? 'A' : 'E');
  const [empfirstname, setEmpFirstName] = useState(userRole === 'admin' ? 'admin' : getUserName());
  const [companyLogo, setCompanyLogo] = useState(raygain);
  const [profilePic, setProfilePic] = useState(null);
  const { logout } = useContext(AuthContext);

  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // SSO states
  const [showSSO, setShowSSO] = useState(false);
  const [ssoSearchQuery, setSsoSearchQuery] = useState("");
  const ssoRef = useRef(null);

  useEffect(() => {
    const storedLogo = getCompanyLogo();
    if (storedLogo) {
      const blob = new Blob([Uint8Array.from(storedLogo.data)], { type: "image/png" });
      const logoUrl = URL.createObjectURL(blob);
      setCompanyLogo(logoUrl);
    }
    setCircleName(userRole === 'admin' ? 'A' : 'E');
    setEmpFirstName(userRole === 'admin' ? 'admin' : getUserName());

    const fetchProfilePic = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token && userRole === 'employee') {
          const res = await fetch(`${API_BASE_URL}/profile/details`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.employee && data.employee.profile_picture) {
              setProfilePic(`${API_BASE_URL}/${data.employee.profile_picture}`);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching profile pic in header:', err);
      }
    };
    fetchProfilePic();
  }, [userRole]);

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle click outside SSO panel
  useEffect(() => {
    const handleClickOutsideSSO = (event) => {
      if (ssoRef.current && !ssoRef.current.contains(event.target)) {
        setShowSSO(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideSSO);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSSO);
    };
  }, []);

  // Filter items matching query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const matches = searchItems.filter(item => 
      item.roles.includes(userRole) && 
      (item.label.toLowerCase().includes(query) || item.category.toLowerCase().includes(query))
    );

    setFilteredItems(matches);
    setSelectedIndex(0);
  }, [searchQuery, userRole]);

  const handleSearchSelect = (item) => {
    navigate(item.route);
    setSearchQuery("");
    setShowDropdown(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        handleSearchSelect(filteredItems[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleToggle = () => {
    setToggle(!toggle);
  };

  return (
    <header>
      <div className="header-left">
        <div className="toggles">
          {toggle ? <MenuIcon onClick={handleToggle} /> : <CloseIcon onClick={handleToggle} />}
        </div>
        <img src={companyLogo} alt="Raygain Logo" style={{ cursor: 'pointer' }} onClick={() => navigate("/dashboard")} />
      </div>

      <div className="search-container" ref={searchRef}>
        <input 
          type="text" 
          placeholder="Search across HRMS screens..." 
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
        />
        {showDropdown && searchQuery.trim() && (
          <div className="search-dropdown">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, idx) => (
                <div 
                  key={idx}
                  className={`search-dropdown-item ${idx === selectedIndex ? "selected" : ""}`}
                  onClick={() => handleSearchSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="search-dropdown-label">{item.label}</span>
                  <span className="search-dropdown-category">{item.category}</span>
                </div>
              ))
            ) : (
              <div className="search-dropdown-empty">No matching screens found</div>
            )}
          </div>
        )}
      </div>

      <div
        className="profile"
        style={{ cursor: userRole === 'admin' ? 'default' : 'pointer' }}
        onClick={userRole === 'admin' ? undefined : () => navigate("/profile")}
      >
        <div className="profile-avatar">
          {profilePic ? (
            <img src={profilePic} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            circlename
          )}
        </div>
        <span className="profile-name">{empfirstname}</span>
        <div className="logout-btn" title="Logout" onClick={(e) => { e.stopPropagation(); logout(); }}>
          <LogoutIcon />
        </div>
        <div className="sso-btn" title="Raygain Apps" onClick={(e) => { e.stopPropagation(); setShowSSO(!showSSO); }}>
          <RaygainSSOIcon />
        </div>
      </div>

      {showSSO && (
        <div className="sso-panel" ref={ssoRef} onClick={(e) => e.stopPropagation()}>
          <div className="sso-header">
            <div className="sso-search-container">
              <span className="sso-search-icon">
                <svg width="30" height="14" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <input
                type="text"
                className="sso-search-input"
                placeholder="Search Applications"
                value={ssoSearchQuery}
                onChange={(e) => setSsoSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="sso-content">
            {/* Featured App */}
            {(!ssoSearchQuery.trim() || "projexa".includes(ssoSearchQuery.toLowerCase())) && (
              <div className="sso-featured-section">
                <span className="sso-section-title">Featured App</span>
                <div className="sso-featured-card">
                  <div className="sso-featured-icon">
                    <AssignmentIcon style={{ fontSize: '20px' }} />
                  </div>
                  <div className="sso-featured-details">
                    <span className="sso-featured-name">Projexa</span>
                    <span className="sso-featured-desc">Project Management software to accelerate execution.</span>
                    <a href="https://projexa.raygain.com/login" target="_blank" rel="noopener noreferrer" className="sso-featured-link">Try now</a>
                  </div>
                </div>
                
                {/* Promo Banner inside Featured section */}
                <div className="sso-promo-banner">
                  <span className="sso-promo-tag">Automation</span>
                  <span className="sso-promo-text">
                    Build smarter email workflows with <a href="#" className="sso-promo-link" onClick={(e) => e.preventDefault()}>Clientra Scripting</a> in Raygain Mail.
                  </span>
                  <div className="sso-promo-dots">
                    <div className="sso-promo-dot active"></div>
                    <div className="sso-promo-dot"></div>
                    <div className="sso-promo-dot"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Raygain Apps Categories */}
            {ssoCategories.map((cat, catIdx) => {
              const filteredApps = cat.apps.filter(app => 
                app.name.toLowerCase().includes(ssoSearchQuery.toLowerCase()) ||
                app.desc.toLowerCase().includes(ssoSearchQuery.toLowerCase())
              );

              if (filteredApps.length === 0) return null;

              return (
                <div className="sso-category-section" key={catIdx}>
                  <span className="sso-section-title">{cat.category}</span>
                  <div className="sso-apps-grid">
                    {filteredApps.map((app, appIdx) => (
                      <div 
                        className="sso-app-item" 
                        key={appIdx} 
                        title={app.desc}
                        onClick={() => {
                          if (app.link && app.link !== "#") {
                            window.open(app.link, "_blank");
                          }
                        }}
                      >
                        <div className="sso-app-icon-wrapper" style={{ background: app.color }}>
                          {app.icon}
                        </div>
                        <div className="sso-app-text-wrapper">
                          <span className="sso-app-name">{app.name}</span>
                          <span className="sso-app-desc">{app.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Empty state */}
            {ssoCategories.every(cat => 
              cat.apps.filter(app => 
                app.name.toLowerCase().includes(ssoSearchQuery.toLowerCase()) ||
                app.desc.toLowerCase().includes(ssoSearchQuery.toLowerCase())
              ).length === 0
            ) && !("projexa".includes(ssoSearchQuery.toLowerCase())) && (
              <div className="sso-empty">
                No applications found for "{ssoSearchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
